/**
 * @brisa/ui · BrandSquiggle
 *
 * Linha à mão livre (Lilás Bruma) sobre canvas. Assinatura cultural P3.
 * Usa o asset SVG real exportado do Figma (`/brand/squiggle-stroke.svg`)
 * via `<img>` simples — leve, sem custo de hidratação.
 *
 * Para pontos onde o draw-in animado é crítico (tela register), passe
 * `inline` com path simples — animation via stroke-dashoffset CSS.
 */
'use client';

import * as React from 'react';

export interface BrandSquiggleProps {
  variant?: 'horizontal' | 'vertical' | 'underline';
  width?: number;
  height?: number;
  animate?: boolean;
  className?: string;
}

// Simple inline path (placeholder genérico) usado para animar.
const PATHS = {
  horizontal: 'M 4 12 Q 32 4, 56 12 T 112 14 T 168 10 T 232 12',
  vertical: 'M 12 4 Q 4 32, 12 56 T 14 112 T 10 168 T 12 232',
  underline: 'M 4 8 Q 24 14, 56 8 Q 96 2, 140 10 Q 196 18, 236 8',
} as const;

export function BrandSquiggle({
  variant = 'horizontal',
  width = 240,
  height = 24,
  animate = true,
  className,
}: BrandSquiggleProps) {
  const ref = React.useRef<SVGPathElement | null>(null);
  const dim = variant === 'vertical' ? { w: height, h: width } : { w: width, h: height };

  React.useEffect(() => {
    if (!animate || !ref.current) return;
    const path = ref.current;
    const length = path.getTotalLength();
    path.style.strokeDasharray = `${length}`;
    path.style.strokeDashoffset = `${length}`;
    const t = setTimeout(() => {
      path.style.transition =
        'stroke-dashoffset var(--brisa-dur-xslow) var(--brisa-ease-out), opacity var(--brisa-dur-slow) var(--brisa-ease-out)';
      path.style.strokeDashoffset = '0';
      path.style.opacity = 'var(--brisa-squiggle-opacity)';
    }, 500);
    return () => clearTimeout(t);
  }, [animate]);

  return (
    <svg
      width={dim.w}
      height={dim.h}
      viewBox={`0 0 ${dim.w} ${dim.h}`}
      role="presentation"
      aria-hidden="true"
      className={['brisa-brand-squiggle', className].filter(Boolean).join(' ')}
      style={{ display: 'block' }}
    >
      <path
        ref={ref}
        d={PATHS[variant]}
        stroke="var(--brisa-squiggle-color)"
        strokeWidth="var(--brisa-squiggle-weight)"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
        opacity={animate ? 0 : 'var(--brisa-squiggle-opacity)'}
      />
    </svg>
  );
}
